
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Notification } from '@/types/notifications';
import { useAuth } from '@/contexts/auth';
import { toast } from 'sonner';

interface RealtimeNotificationsProps {
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
  setUnreadCount: React.Dispatch<React.SetStateAction<number>>;
  fetchNotifications: () => Promise<Notification[]>;
}

export const useRealtimeNotifications = ({
  setNotifications,
  setUnreadCount,
  fetchNotifications
}: RealtimeNotificationsProps) => {
  const { user } = useAuth();

  // Set up real-time subscription
  useEffect(() => {
    if (!user?.id) return;
    
    console.log('Setting up real-time notifications subscription for user:', user.id);
    
    // Create a channel with the user ID and timestamp to ensure uniqueness
    const channelName = `notifications-${user.id}-${Date.now()}`;
    
    // First, do an immediate fetch to ensure we have the latest data
    fetchNotifications().catch(err => console.error('Initial notification fetch error:', err));
    
    const subscription = supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `recipient_id=eq.${user.id}`
      }, async (payload) => {
        console.log('New notification received via realtime:', payload);
        
        try {
          // Double-check this notification is for the current user
          if (!payload.new || payload.new.recipient_id !== user.id) {
            console.warn('Received notification for different user, ignoring');
            return;
          }
          
          // Get the new notification
          const newNotification = payload.new as Notification;
          
          // Get sender profile if there's a sender_id
          if (newNotification.sender_id) {
            try {
              const { data: senderProfile, error: senderError } = await supabase
                .from('profiles')
                .select('username, full_name, avatar_url')
                .eq('id', newNotification.sender_id)
                .single();
                
              if (senderProfile) {
                newNotification.sender_name = senderProfile.full_name || senderProfile.username || 'User';
                newNotification.sender_avatar = senderProfile.avatar_url;
              } else if (senderError) {
                console.error('Error fetching sender profile:', senderError);
                newNotification.sender_name = 'User';
              }
            } catch (err) {
              console.error('Error in sender profile fetch process:', err);
              newNotification.sender_name = 'User';
            }
          } else {
            // System notification
            newNotification.sender_name = 'System Notification';
          }
          
          // Add the new notification to the state at the top of the list
          setNotifications(prevNotifications => {
            // Check if notification already exists (avoid duplicates)
            const exists = prevNotifications.some(n => n.id === newNotification.id);
            if (exists) {
              return prevNotifications;
            }
            // Always add new notifications at the beginning of the array (newest first)
            return [newNotification, ...prevNotifications];
          });
          
          // Increment unread count
          setUnreadCount(prev => prev + 1);
          
          // Show a toast notification
          const notificationType = newNotification.type === 'system' 
            ? 'System' 
            : newNotification.type.charAt(0).toUpperCase() + newNotification.type.slice(1);
          
          toast(
            `New ${notificationType} Notification`,
            {
              description: newNotification.content,
            }
          );
        } catch (error) {
          console.error('Error processing realtime notification:', error);
          // Fallback to fetching all notifications if there's an error
          fetchNotifications().catch(console.error);
        }
      })
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
        
        if (status !== 'SUBSCRIBED') {
          console.warn('Realtime subscription failed with status:', status);
          // Try to re-fetch notifications as a fallback
          setTimeout(() => {
            fetchNotifications().catch(console.error);
          }, 2000);
        } else {
          console.log('Successfully subscribed to real-time notifications');
        }
      });

    return () => {
      console.log('Unsubscribing from real-time notifications');
      supabase.removeChannel(subscription);
    };
  }, [user?.id, setNotifications, setUnreadCount, fetchNotifications]);

  return { fetchNotifications };
};
